// ============================================================================
// src/seanceDiff.js : ce que la console de séance envoie au serveur
// ============================================================================
// Fermeture des règles, lot 4 (18/10/2026). La console écrivait sessions/current
// EN ENTIER (setDoc sans fusion) : le dernier poste à écrire effaçait ce que
// les autres avaient écrit depuis son dernier instantané (votes, émargements,
// clôture d'un scrutin). Elle calcule désormais la DIFFÉRENCE entre ce qu'elle
// affichait et ce qu'elle veut écrire, et la fonction seanceAnimer l'applique,
// en transaction, au document tel qu'il est (functions/lib/seanceDiff.js).
//
// calculerDiff(base, suivant, anciens) — `anciens` : les instantanés PRÉCÉDENTS
// (du plus récent au plus ancien). Un écran resté un rendu en arrière écrit
// `{ ...appState, x }` avec un appState périmé : chaque valeur qu'il n'a PAS
// touchée est alors la même référence qu'au MÊME endroit d'un instantané
// précédent. Elle n'est pas envoyée (sinon elle effacerait ce qu'un autre
// poste a écrit depuis). Dans une liste d'objets à `id`, un élément absent du
// suivant n'est retiré que s'il figurait dans la liste que l'écran a vue :
// un vote arrivé depuis n'est jamais effacé par une console en retard.
//
// appliquerDiff : MÊME algorithme que le serveur (test de parité :
// src/seanceDiffParite.test.js). L'écran l'applique par avance sur
// l'instantané reçu (affichage immédiat) ; la vérité reste celle du serveur.
// ============================================================================

const PROFONDEUR_MAX = 12;
const CLES_INTERDITES = new Set(["__proto__", "constructor", "prototype"]);

export const estObjet = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const copie = (v) => (v === undefined ? null : JSON.parse(JSON.stringify(v)));

/** Égalité profonde de données JSON (l'ordre des clés d'un objet n'importe pas). */
export function egal(a, b) {
  if (a === b) return true;
  if (typeof a !== typeof b || a === null || b === null || typeof a !== "object") return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) return a.length === b.length && a.every((x, i) => egal(x, b[i]));
  const ka = Object.keys(a).filter((k) => a[k] !== undefined);
  const kb = Object.keys(b).filter((k) => b[k] !== undefined);
  return ka.length === kb.length && ka.every((k) => Object.prototype.hasOwnProperty.call(b, k) && egal(a[k], b[k]));
}

function listeParId(arr) {
  if (!Array.isArray(arr)) return null;
  const m = new Map();
  for (const x of arr) {
    if (!estObjet(x) || typeof x.id !== "string" || !x.id || m.has(x.id)) return null;
    m.set(x.id, x);
  }
  return m;
}
const enfant = (v, k) => (estObjet(v) ? v[k] : undefined);
const elementDe = (liste, id) => (Array.isArray(liste) ? liste.find((x) => estObjet(x) && x.id === id) : undefined);

function noeud(base, suivant, anciens, prof) {
  if (suivant === base) return undefined;
  // Valeur périmée : la même référence qu'au même endroit d'un instantané
  // précédent, donc rien que l'écran ait changé.
  if (suivant !== null && typeof suivant === "object" && anciens.some((a) => a === suivant)) return undefined;
  if (egal(base, suivant)) return undefined;
  if (prof < PROFONDEUR_MAX && estObjet(base) && estObjet(suivant)) {
    const o = {};
    const d = [];
    for (const k of Object.keys(suivant)) {
      if (suivant[k] === undefined) { if (base[k] !== undefined) d.push(k); continue; }
      if (base[k] === undefined) { o[k] = { $v: copie(suivant[k]) }; continue; }
      const n = noeud(base[k], suivant[k], anciens.map((a) => enfant(a, k)), prof + 1);
      if (n !== undefined) o[k] = n;
    }
    for (const k of Object.keys(base)) if (base[k] !== undefined && !Object.prototype.hasOwnProperty.call(suivant, k)) d.push(k);
    if (!Object.keys(o).length && !d.length) return undefined;
    return d.length ? { $o: o, $d: d } : { $o: o };
  }
  if (prof < PROFONDEUR_MAX && Array.isArray(base) && Array.isArray(suivant)) {
    const mb = listeParId(base);
    const ms = listeParId(suivant);
    if (mb && ms) {
      // La liste que l'écran a vue : celle qui partage le plus d'éléments (par
      // référence) avec ce qu'il écrit, puis qui suppose le moins de retraits,
      // puis la plus récente ; à défaut, l'état courant.
      const idsSuivant = new Set(ms.keys());
      let vue = base;
      let meilleur = null;
      for (const l of [base, ...anciens]) {
        if (!Array.isArray(l)) continue;
        const communs = suivant.filter((x) => l.includes(x)).length;
        if (!communs) continue;
        const retraits = l.filter((x) => estObjet(x) && !idsSuivant.has(x.id)).length;
        if (!meilleur || communs > meilleur[0] || (communs === meilleur[0] && retraits < meilleur[1])) {
          meilleur = [communs, retraits]; vue = l;
        }
      }
      const idsVus = new Set((Array.isArray(vue) ? vue : []).map((x) => estObjet(x) && x.id).filter(Boolean));
      const maj = {};
      const ajouts = [];
      const retires = [];
      for (const [id, item] of ms) {
        if (mb.has(id)) {
          const n = noeud(mb.get(id), item, anciens.map((a) => elementDe(a, id)), prof + 1);
          if (n !== undefined) maj[id] = n;
        } else if (!(idsVus.has(id) && vue !== base)) {
          ajouts.push(copie(item));                 // vraiment neuf (pas un élément retiré depuis par un autre poste)
        }
      }
      for (const id of mb.keys()) if (!ms.has(id) && idsVus.has(id)) retires.push(id);
      const attendu = [...base.map((x) => x.id).filter((id) => ms.has(id)), ...ajouts.map((x) => x.id)];
      const reel = suivant.map((x) => x.id).filter((id) => mb.has(id) || ajouts.some((a) => a.id === id));
      const l = {};
      if (Object.keys(maj).length) l.maj = maj;
      if (ajouts.length) l.ajouts = ajouts;
      if (retires.length) l.retires = retires;
      if (!egal(attendu, reel)) l.ordre = reel;
      return Object.keys(l).length ? { $l: l } : undefined;
    }
  }
  return { $v: copie(suivant) };
}

/** Le diff de `base` (ce que l'écran affiche) à `suivant` (ce qu'il veut
 *  écrire), ou null s'il n'y a rien à écrire. */
export function calculerDiff(base, suivant, anciens = []) {
  const n = noeud(estObjet(base) ? base : {}, estObjet(suivant) ? suivant : {}, anciens.filter(Boolean), 0);
  if (!n) return null;
  return n.$o || n.$d ? n : { $o: {} };
}

// ── Application : miroir EXACT de functions/lib/seanceDiff.js ───────────────
function appliquerNoeud(cur, n) {
  if (!estObjet(n)) return cur;
  if (Object.prototype.hasOwnProperty.call(n, "$v")) return copie(n.$v);
  if (n.$o || n.$d) {
    const out = estObjet(cur) ? { ...cur } : {};
    const o = estObjet(n.$o) ? n.$o : {};
    for (const k of Object.keys(o)) {
      if (CLES_INTERDITES.has(k)) continue;
      out[k] = appliquerNoeud(out[k], o[k]);
    }
    for (const k of (Array.isArray(n.$d) ? n.$d : [])) {
      if (typeof k === "string" && !CLES_INTERDITES.has(k)) delete out[k];
    }
    return out;
  }
  if (n.$l) {
    const l = n.$l;
    let liste = Array.isArray(cur) ? cur.slice() : [];
    const retires = new Set(Array.isArray(l.retires) ? l.retires : []);
    if (retires.size) liste = liste.filter((x) => !(estObjet(x) && retires.has(x.id)));
    if (estObjet(l.maj)) {
      liste = liste.map((x) => (estObjet(x) && typeof x.id === "string"
        && Object.prototype.hasOwnProperty.call(l.maj, x.id) ? appliquerNoeud(x, l.maj[x.id]) : x));
    }
    for (const a of (Array.isArray(l.ajouts) ? l.ajouts : [])) {
      if (!estObjet(a)) continue;
      const i = liste.findIndex((x) => estObjet(x) && x.id === a.id);
      if (i >= 0) liste[i] = copie(a); else liste.push(copie(a));
    }
    if (Array.isArray(l.ordre)) {
      const rang = new Map(l.ordre.map((id, i) => [id, i]));
      const dansOrdre = liste.filter((x) => estObjet(x) && rang.has(x.id))
        .sort((a, b) => rang.get(a.id) - rang.get(b.id));
      const autres = liste.filter((x) => !(estObjet(x) && rang.has(x.id)));
      liste = [...dansOrdre, ...autres];
    }
    return liste;
  }
  return cur;
}

/** Applique un diff (nœud racine) à l'état ; rend un NOUVEL état. */
export function appliquerDiff(etat, diff) {
  if (!diff) return etat;
  const r = appliquerNoeud(etat, diff);
  return estObjet(r) ? r : {};
}
